import React, {useState} from 'react'
import {css} from '@emotion/react'
import axios from 'axios'
import {LOGGING_BASE_URL} from '../../../../constants'
import {createMarkup} from '../../../../util/datoCmsDataFactory'

const text = {
    en: {
        buttonText: `Join the waitlist`,
        failureMessage: `Something went wrong.`,
        failureBody: `There was an error in your request. Please try again or email <a href="mailto: privacyplease@startpage.com">privacyplease@startpage.com</a> to be manually added to the list.`,
        bodyText: `Be the first to try our NEW search and browsing app.`,
        thanksMessage: `Thanks for joining the waitlist!`,
        emailInput: `Enter email`,
        submitButton: `Submit`,
    },
    de: {
        buttonText: `Melde dich für die Warteliste an`,
        failureMessage: `Etwas ist schiefgelaufen`,
        failureBody: `Es ist ein Fehler aufgetreten. Bitte versuche es erneut oder schreibe eine E-Mail <a href="mailto: privacyplease@startpage.com">privacyplease@startpage.com</a>, um manuell zu der Liste hinzugefügt zu werden.`,
        bodyText: `Sei unter den Ersten, die unsere NEUE Such- und Browser-App testen.`,
        thanksMessage: `Danke, dass du dich für die Warteliste angemeldet hast`,
        emailInput: `E-Mail-Adresse angeben`,
        submitButton: `Abschicken`,
    },
    nl: {
        buttonText: `Meld je aan voor de wachtlijst`,
        failureMessage: `Er is iets fout gegaan.`,
        failureBody: `Er is een fout opgetreden. Probeer het opnieuw of stuur een e-mail naar <a href="mailto: privacyplease@startpage.com">privacyplease@startpage.com</a> om handmatig aan de lijst te worden toegevoegd.`,
        bodyText: `Probeer als eerste onze nieuwe browser app.`,
        thanksMessage: `Bedankt voor het inschrijven op de wachtlijst!`,
        emailInput: `E-mailadres invoeren`,
        submitButton: `Versturen`,
    },
}

const Waitlist = ({languageCode = `en`}) => {
    // Constants
    const [submitted, setSubmitted] = useState(false)
    const [email, setEmail] = useState('')
    const [success, setSuccess] = useState(0) // Value 0 = neither succeeded nor failed; Value 1 = success; Value 2 = failure

    // Functions
    const handleSubmit = (userEmail) => async (event) => {
        event.preventDefault()
        setSubmitted(true)
        try {
            await axios
                .post(`${LOGGING_BASE_URL}/do/browser-newsletter-signup`, {
                    email: userEmail,
                    lang: languageCode,
                })
                .then(() => {
                    setSuccess(1)
                })
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log('error', error)
            setSuccess(2)
        }
    }

    const handleInput = (event) => {
        setEmail(event.target.value)
    }

    const setText = () => {
        if (success === 2) {
            return text[languageCode].failureBody
        }
        if (success === 0 && submitted) {
            return ''
        }

        return text[languageCode].bodyText
    }

    // Render
    return (
        <div
            id="waitlist-section"
            css={css`
                height: 402px;
                width: 100%;
                position: relative;
            `}
        >
            <div
                className="background"
                css={css`
                    background: linear-gradient(96.17deg, #e8eaff 13.38%, #ecfffd 85.76%);
                    transform: matrix(-1, 0, 0, 1, 0, 0);
                    position: absolute;
                    height: 100%;
                    width: 100%;
                    z-index: 0;
                `}
            />
            <div
                css={css`
                    z-index: 4;
                    position: relative;
                    background-image: url(https://www.startpage.com/sp/cdn/images/marketing-waitlist-background.svg);
                    background-repeat: no-repeat;
                    background-size: cover;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <h3
                    css={css`
                        font-size: 40px;
                        line-height: 40px;
                        font-weight: 800;
                    `}
                >
                    {(success === 1 || success === 0) && text[languageCode].buttonText}
                    {success === 2 && text[languageCode].failureMessage}
                </h3>
                <p
                    css={css`
                        font-size: 16px;
                        margin: 18px 0 34px;
                        line-height: 24px;
                        max-width: 520px;
                        text-align: center;
                        a {
                            color: #2e39b3;
                        }
                    `}
                    dangerouslySetInnerHTML={createMarkup(setText())}
                />
                {submitted ? (
                    <>
                        {success === 0 && (
                            <img
                                src="https://www.startpage.com/sp/cdn/images/waitlist-loading-icon.svg"
                                alt="loading icon"
                                css={css`
                                    @keyframes spinner {
                                        0% {
                                            transform: translate3d(-50%, -50%, 0) rotate(0deg);
                                        }
                                        100% {
                                            transform: translate3d(-50%, -50%, 0) rotate(360deg);
                                        }
                                    }
                                    animation: 1s linear infinite spinner;
                                `}
                            />
                        )}
                        <p
                            css={css`
                                font-size: 18px;
                                line-height: 24px;
                                margin: 16px 0 0;
                                color: #5b627a;
                            `}
                        >
                            {success === 1 && text[languageCode].thanksMessage}
                        </p>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(email)}>
                        <input
                            type="email"
                            css={css`
                                width: 440px;
                                height: 62px;
                                background-color: #ffffff;
                                border: 1px solid #dee0f7;
                                box-shadow: 0px 20px 26px rgba(0, 0, 0, 0.1);
                                border-radius: 54px;
                                padding: 0 27px;
                                margin-right: 24px;
                                font-size: 16px;
                                line-height: 24px;
                                &:invalid {
                                    border: 2px solid #e23d3d;
                                }
                            `}
                            placeholder={text[languageCode].emailInput}
                            onChange={handleInput}
                        />
                        <input
                            type="submit"
                            css={css`
                                width: 122px;
                                height: 62px;
                                background-color: #29ddcc;
                                box-shadow: 0px 20px 26px rgba(0, 0, 0, 0.1);
                                border-radius: 10px;
                                border: none;
                                font-size: 18px;
                                line-height: 24px;
                                color: #202c46;
                                font-weight: 600;
                                cursor: pointer;
                                &:hover {
                                    background-color: #51ffee;
                                }
                            `}
                            value={text[languageCode].submitButton}
                        />
                    </form>
                )}
            </div>
        </div>
    )
}

export default Waitlist
